<script>
// Components
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config.json";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import {authComputed} from "@/state/helpers";
import DateUtils from '@/helpers/date';
import UserUtils from '@/helpers/users';
import FinanceUtils from '@/helpers/finance';
import Storage from "@/helpers/storage"

// Services
import FinanceDataService from "@/services/FinanceDataService";
import MarketingDataService from "@/services/MarketingDataService";
import UsersDataService from "@/services/UsersDataService"

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

// Lib
import isEmpty from "is-empty";

const ads = {};
const marketing = {};
const organic = {};
const adsData1 = {};
const adsData2 = {};

export default {
  name: "transactions",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Finance Transactions",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },
  methods: {
    ...UserUtils,
    ...DateUtils,
    ...FinanceUtils,

    handlePageChange(value) {
      this.page = value;
      if (this.transactionsSearch === 'filters')
        this.loadTransactions();
      else if (this.transactionsSearch === 'search')
        this.processSearch(false);
    },

    handleDisplayChange() {
      this.loadTransactions();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getFilters() {
      MarketingDataService.getAdsNonOrganicFilters()
          .then(response => {
            response.data.filters.ads.forEach(ad => {
              this.filters.ads.options.push(ad);
            });
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("marketing.getAdsNonOrganicFilters"));
          });
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }
      else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['gender'] = this.filters.genders.selected;
      params['offer'] = this.filters.promo.selected;
      params['subscription'] = this.filters.subscriptions.selected;
      params['pack'] = this.filters.packs.selected;
      params['paymentMethod'] = this.filters.paymentMethod.selected;
      params['paymentProcessor'] = this.filters.paymentProcessor.selected;
      params['organic'] = this.filters.organic.selected;
      params['countries'] = this.filters.countries.selected;
      params['currency'] = this.filters.currencies.selected;
      if (this.filters.adsData3.selected !== 'all') {
        params['ads'] = this.filters.adsData3.selected;
      }
      else if (this.filters.adsData2.selected !== 'all') {
        params['ads'] = this.filters.adsData2.selected;
      }
      else if (this.filters.adsData1.selected !== 'all') {
        params['ads'] = this.filters.adsData1.selected;
      }
      else {
        params['ads'] = this.filters.ads.selected;
      }

      if (!isEmpty(this.filters.userId)) {
        params['userId'] = this.filters.userId;
      }

      params['status'] = this.filters.status.selected;

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    loadStats() {
      const params = this.getRequestParams();
      FinanceDataService
          .getTransactionsStats(params)
          .then(response => {
            const {total, products, processors, reasons, sdks, qualityUsers, evolGraph, avgDelayGraph, avgFirstGraph} = response.data;
            this.stats.success = total.success;
            this.stats.first = total.first;
            this.stats.renew = total.renew;
            this.stats.fail = total.fail;
            this.stats.cancel = total.cancel;
            this.stats.refund = total.refund;
            this.stats.stopped = total.stopped;
            this.stats.losses = total.losses;
            this.stats.expire = total.expire;
            this.stats.fail_provisions = total.fail_provisions;
            this.stats.fail_others = total.fail_others;
            this.stats.chargeback = total.chargeback;


            this.stats.revenue = total.revenue;
            this.stats.revenue_ht = total.revenue_ht;
            this.stats.revenue_first = total.revenue_first;
            this.stats.revenue_first_ht = total.revenue_first_ht;
            this.stats.revenue_renew = total.revenue_renew;
            this.stats.revenue_renew_ht = total.revenue_renew_ht;
            this.stats.revenue_refund = total.revenue_refund;
            this.stats.revenue_refund_ht = total.revenue_refund_ht;
            this.stats.avgFirst = total.avgFirst;
            this.stats.avgDelay = total.avgDelay;

            this.productsGraph.subs.series = products.subs.series;
            this.productsGraph.subs.chartOptions = {...this.productsGraph.subs.chartOptions, ...{
                labels: products.subs.labels
              }};

            /*
            this.productsGraph.credits.series = products.credits.series;
            this.productsGraph.credits.chartOptions = {...this.productsGraph.credits.chartOptions, ...{
                labels: products.credits.labels
              }};
             */

            this.productsGraph.iceBreakers.series = products.iceBreakers.series;
            this.productsGraph.iceBreakers.chartOptions = {...this.productsGraph.iceBreakers.chartOptions, ...{
                labels: products.iceBreakers.labels
              }};

            this.productsGraph.boosts.series = products.boosts.series;
            this.productsGraph.boosts.chartOptions = {...this.productsGraph.boosts.chartOptions, ...{
                labels: products.boosts.labels
              }};

            this.reasonsGraph.series = reasons.series;
            this.reasonsGraph.chartOptions = {...this.reasonsGraph.chartOptions, ...{
                labels: reasons.labels
              }};

            this.qualityGraph.series = qualityUsers.series;
            this.qualityGraph.chartOptions = {...this.qualityGraph.chartOptions, ...{
                labels: qualityUsers.labels
              }};

            this.sdkGraph.series = sdks.series;
            this.sdkGraph.chartOptions = {...this.sdkGraph.chartOptions, ...{
                labels: sdks.labels
              }};

            /*this.methodsGraph.series = methods.series;
            this.methodsGraph.chartOptions = {...this.methodsGraph.chartOptions, ...{
                labels: methods.labels
              }};*/

            this.processorsGraph.series = processors.series;
            this.processorsGraph.chartOptions = {...this.processorsGraph.chartOptions, ...{
                labels: processors.labels
              }};

            this.$refs.transactionsAvgFirstHistoryChart.updateSeries([{
              name: "Avg Revenue",
              type: 'area',
              data: avgFirstGraph
              }]);

            this.$refs.transactionsAvgDelayHistoryChart.updateSeries([{
              name: "Avg Delay",
              type: 'area',
              data: avgDelayGraph
            }]);

            this.$refs.transactionsEvolSuccessChart.updateSeries([{
              name: "First",
              type: 'bar',
              data: evolGraph.first
            },{
              name: "Renew",
              type: 'bar',
              data: evolGraph.renew
            }, {
              name: "Renew Susp.",
              type: 'bar',
              data: evolGraph.renew_suspended
            }, {
              name: "Refund",
              type: 'bar',
              data: evolGraph.refund
            }]);

            this.$refs.transactionsEvolSuccessChart.updateSeries([{
              name: "First",
              type: 'bar',
              data: evolGraph.first
            },{
              name: "Renew",
              type: 'bar',
              data: evolGraph.renew
            }, {
              name: "Renew Susp.",
              type: 'bar',
              data: evolGraph.renew_suspended
            }, {
              name: "Refund",
              type: 'bar',
              data: evolGraph.refund
            }]);

            this.$refs.transactionsEvolRevenueChart.updateSeries([{
              name: "Revenue",
              type: 'area',
              data: evolGraph.revenueHT
            }, {
              name: "First",
              type: 'line',
              data: evolGraph.revenueFirstHT
            },{
              name: "Renew",
              type: 'line',
              data: evolGraph.revenueRenewHT
            }, {
              name: "Refund",
              type: 'line',
              data: evolGraph.revenueRefundHT
            }]);

            this.$refs.transactionsEvolFailChart.updateSeries([{
              name: "Fail",
              type: 'bar',
              data: evolGraph.fail
            }, {
              name: "Fail Renew",
              type: 'bar',
              data: evolGraph.fail_renew
            }]);

            this.$refs.transactionsEvolCancelChart.updateSeries([{
              name: "Cancel",
              type: 'bar',
              data: evolGraph.cancel
            }, {
              name: "Expired",
              type: 'bar',
              data: evolGraph.ended
            }]);
          })
          .catch(error => {
            window.console.error(error);
          });
    },

    getMediaThumb() {
      if (this.transactions.length > 0)
        this.transactions.forEach(trans => {
          if (trans.photo && trans.photo.length > 0)
            Storage.getUserMedia(trans.userId, trans.photo, 'transaction-user-'+trans.id);

        });
    },

    trunc(value) {
      if (!isEmpty(value)) {
        if (value.length > 70)
          return value.substring(0, 70) + "…";
        else
          return value;
      }
      else {
        return "";
      }
    },

    loadTransactions() {
      this.isLoading = true;
      const params = this.getRequestParams();
      this.transactionsSearch = 'filters';
      FinanceDataService
          .getTransactions(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.transactions = items;
            this.count = totalItems;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("finance.errorTransactionsNoResult"));
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    loadDetails(transactionId) {
      this.$toast.warning("loadDetails not implemented: " + transactionId);
    },

    searchUser(userId) {
      this.filters.searchType.selected = 'userId';
      this.filters.searchData = userId;
      this.processSearch(true);
    },

    searchOrder(orderId) {
      this.filters.searchType.selected = 'orderId';
      this.filters.searchData = orderId;
      this.processSearch(true);
    },

    processFilters() {
      this.loadTransactions();
      this.loadStats();
    },

    processSearch(resetPage) {
      if (isEmpty(this.filters.searchType)) {
        this.$toast.error(this.$t("finance.error_searchType"));
        return;
      }

      if (isEmpty(this.filters.searchData)) {
        this.$toast.error(this.$t("finance.error_searchData"));
        return;
      }

      this.transactionsSearch = 'search';
      if (resetPage) {
        this.page = 1;
      }

      this.isLoading = true;
      const params = {
        type: this.filters.searchType.selected,
        data: this.filters.searchData,
        page: this.page - 1,
        size: this.pageSizes.selected,
      };

      FinanceDataService
          .searchTransactions(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.transactions = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.error(error);
            this.$toast.error(this.$t("finance.errorTransactionsNoResult"));
            this.isLoading = false;
          });
    },

    _isEmpty(val) {
      return isEmpty(val);
    },

    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },
  mounted() {
    this.getFilters();
    this.processFilters();
  },
  computed: {
    ...authComputed
  },
  watch: {
    "filters.organic.selected": function (newVal, oldVal) {
      if (oldVal === newVal)
        return;

      if (newVal === "organic") {
        this.filters.ads.selected = 'all';
        this.filters.ads.options = [{
          value: 'all', text: 'All Ads'
        }];
        if (isEmpty(organic)) {
          MarketingDataService.getAdsOrganicFilters()
              .then(response => {
                response.data.filters.ads.forEach(ad => {
                  this.filters.ads.options.push(ad);
                });

                organic.ads = response.data.filters.ads;
              })
              .catch(error => {
                window.console.log(error);
                this.$toast.error(this.$t("marketing.getAdsOrganicFilters"));
              });
        }
        else {
          organic.ads.forEach(ad => {
            this.filters.ads.options.push(ad);
          });
        }
      }
      else if (['marketing', 'all'].includes(newVal) && oldVal === "organic") {
        this.filters.ads.selected = 'all';
        this.filters.ads.options = [{
          value: 'all', text: 'All Ads'
        }];
        if (!isEmpty(marketing)) {
          marketing.ads.forEach(ad => {
            this.filters.ads.options.push(ad);
          });
        }
      }
    },
    "filters.ads.selected": function (newVal, oldVal) {
      if (oldVal === newVal || newVal === 'all')
        return;

      this.filters.adsData1.selected = 'all';
      this.filters.adsData2.selected = 'all';
      this.filters.adsData3.selected = 'all';
      this.filters.adsData1.options = [{
        value: 'all', text: 'All Data1'
      }];
      this.filters.adsData2.options = [{
        value: 'all', text: 'All Data2'
      }];
      this.filters.adsData3.options = [{
        value: 'all', text: 'All Data3'
      }];

      if (isEmpty(ads[newVal])) {
        MarketingDataService.getAdsNonOrganicData1Filters(newVal)
            .then(response => {
              marketing.ads = response.data.filters.ads;
              response.data.filters.ads.forEach(ad => {
                this.filters.adsData1.options.push(ad);
              });

              ads[newVal] = response.data.filters.ads;
            })
            .catch(error => {
              window.console.log(error);
              this.$toast.error(this.$t("marketing.getAdsNonOrganicData1Filters"));
            });
      }
      else {
        ads[newVal].forEach(ad => {
          this.filters.adsData1.options.push(ad);
        });
      }
    },
    "filters.adsData1.selected": function (newVal, oldVal) {
      if (oldVal === newVal || newVal === 'all')
        return;

      this.filters.adsData2.selected = 'all';
      this.filters.adsData3.selected = 'all';
      this.filters.adsData2.options = [{
        value: 'all', text: 'All Data2'
      }];
      this.filters.adsData3.options = [{
        value: 'all', text: 'All Data3'
      }];

      if (isEmpty(adsData1[newVal])) {
        MarketingDataService.getAdsNonOrganicData2Filters(newVal)
            .then(response => {
              response.data.filters.ads.forEach(ad => {
                this.filters.adsData2.options.push(ad);
              });

              adsData1[newVal] = response.data.filters.ads;
            })
            .catch(error => {
              window.console.log(error);
              this.$toast.error(this.$t("marketing.getAdsNonOrganicData2Filters"));
            });
      }
      else {
        adsData1[newVal].forEach(ad => {
          this.filters.adsData2.options.push(ad);
        });
      }
    },
    "filters.adsData2.selected": function (newVal, oldVal) {
      if (oldVal === newVal || newVal === 'all')
        return;

      this.filters.adsData3.selected = 'all';
      this.filters.adsData3.options = [{
        value: 'all', text: 'All Data3'
      }];

      if (isEmpty(adsData2[newVal])) {
        MarketingDataService.getAdsNonOrganicData3Filters(newVal)
            .then(response => {
              response.data.filters.ads.forEach(ad => {
                this.filters.adsData3.options.push(ad);
              });

              adsData2[newVal] = response.data.filters.ads;
            })
            .catch(error => {
              window.console.log(error);
              this.$toast.error(this.$t("marketing.getAdsNonOrganicData3Filters"));
            });
      }
      else {
        adsData2[newVal].forEach(ad => {
          this.filters.adsData3.options.push(ad);
        });
      }
    }
  },

  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.finance.transactions"),
      transactionsSearch: 'filters',
      transactions: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '50'
      },
      stats: {
        success: '-',
        first: '-',
        renew: '-',
        fail: '-',
        cancel: '-',
        refund: '-',
        stopped: "-",
        losses: '-',
        expire: "-",
        fail_provisions: "-",
        fail_others: "-",
        chargeback: "-",
        revenue: '-',
        revenue_ht: '-',
        revenue_first: '-',
        revenue_first_ht: '-',
        revenue_renew: '-',
        revenue_renew_ht: '-',
        revenue_refund: '-',
        revenue_refund_ht: '-',
        avgFirst: '-',
        avgDelay: '-',
      },
      productsGraph: {
        subs: {
          series: [],
          chartOptions: {
            chart: {
              id: 'transactions-subs',
              type: 'donut',
            },
            dataLabels: {
              enabled: true
            },
            labels: [],
            noData: {
              text: 'Loading...'
            },
            legend: {
              position: 'left'
            },
          }
        },
        /*
        credits: {
          series: [],
          chartOptions: {
            chart: {
              id: 'transactions-packs',
              type: 'donut',
            },
            dataLabels: {
              enabled: true
            },
            labels: [],
            noData: {
              text: 'Loading...'
            },
            legend: {
              position: 'left'
            },
          }
        },
        */
        iceBreakers: {
          series: [],
          chartOptions: {
            chart: {
              id: 'transactions-iceBreakers',
              type: 'donut',
            },
            dataLabels: {
              enabled: true
            },
            labels: [],
            noData: {
              text: 'Loading...'
            },
            legend: {
              position: 'left'
            },
          }
        },
        boosts: {
          series: [],
          chartOptions: {
            chart: {
              id: 'transactions-boosts',
              type: 'donut',
            },
            dataLabels: {
              enabled: true
            },
            labels: [],
            noData: {
              text: 'Loading...'
            },
            legend: {
              position: 'left'
            },
          }
        }
      },
      qualityGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'transactions-quality',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'left'
          },
        }
      },
      sdkGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'transactions-sdks',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'left'
          },
        }
      },
      avgFirstGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: 'area',
            toolbar: {
              show: true,
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
            width: 1,
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [
            {
              show: false,
              axisTicks: {
                show: false
              },
              title: {
                text: "Avg Revenue"
              },
            },
          ],
          colors: ["#556ee6"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      avgDelayGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: 'area',
            toolbar: {
              show: true,
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'smooth',
            width: 1,
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [
            {
              show: false,
              axisTicks: {
                show: false
              },
              title: {
                text: "Days"
              },
            },
          ],
          colors: ["#556ee6"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },

      /*methodsGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'transactions-methods',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'left'
          },
        }
      },*/
      reasonsGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'transactions-reasons',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'left'
          },
        }
      },
      processorsGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'transactions-processors',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'left'
          },
        }
      },
      successGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: "bar",
            stacked: true,
            toolbar: {
              show: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
              borderRadius: 10,
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: '13px',
                  }
                }
              }
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true
            },
            title: {
              text: "Nb"
            }
          }],
          colors: ["#34c38f", "#556ee6", "#50a5f1", "#f46a6a"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      revenueGraph: {
        series: [],
        chartOptions: {
          chart: {
            type: 'line',
            toolbar: {
              show: true,
            },
          },
          markers: {
            size: 0,
          },
          stroke: {
            curve: "smooth",
            width: 3,
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          colors: ["#333333", "#34c38f", "#556ee6", "#f46a6a"],
          legend: {
            position: "bottom",
          },
          fill: {
            type:'solid',
            opacity: [0.35, 1],
          },
        },
      },
      failGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            type: 'bar',
            toolbar: {
              show: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
              borderRadius: 10,
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: '13px',
                  }
                }
              }
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true
            },
            title: {
              text: "Nb"
            }
          }],
          colors: ["#666666", "#333333"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      cancelGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            type: 'bar',
            toolbar: {
              show: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
              borderRadius: 10,
              dataLabels: {
                total: {
                  enabled: true,
                  style: {
                    fontSize: '13px',
                  }
                }
              }
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            shared: true,
            intersect: false
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true
            },
            title: {
              text: "Nb"
            }
          }],
          colors: ["#e83e8c", "#efa2c5"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      filters: {
        searchType: {
          selected: 'userId',
          options: [
            {value:'userId', text:this.$t("userId")},
            {value:'orderId', text:this.$t("orderId")},
            {value:'email', text:this.$t("email")}
          ]
        },
        searchData: "",
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'today',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        currencies: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Currencies'},
            {value: '', text: '----------------'},
            {value: 'EUR', text: 'EUR - €'},
            {value: 'CHF', text: 'CHF - Frs'},
            {value: 'CAD', text: 'CAD - $CAD'},
            {value: '', text: '----------------'},
            {value: 'others', text: 'Others'},
          ]
        },
        countries: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Countries'},
            {value: '', text: '----------------'},
            {value: 'FR', text: 'FR - France'},
            {value: 'BE', text: 'BE - Belgium'},
            {value: 'CH', text: 'CH - Switzerland'},
            {value: 'CA', text: 'CA - Canada'},
            {value: '', text: '----------------'},
            {value: 'others', text: 'Others'},
          ]
        },
        paymentMethod: {
          selected: '',
          options: [
            {value: '', text: this.$t("finance.method_all")},
            {value: 'C', text: this.$t("finance.method_card")},
            {value: 'S', text: this.$t("finance.method_store")},
          ]
        },
        paymentProcessor: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("finance.processor_all")},
            {value: 'payplug', text: this.$t("finance.processor_payplug")},
            {value: 'stripe', text: this.$t("finance.processor_stripe")},
            {value: 'hipay', text: this.$t("finance.processor_hipay")},
            {value: '', text: '--------------'},
            {value: 'GP', text: this.$t("finance.processor_google")},
            {value: 'AP', text: this.$t("finance.processor_apple")},
          ]
        },
        promo: {
          selected: 'all',
          options: [
            {value: 'all', text: "All products"},
            {value: 1, text: "Only Promo"},
            {value: 0, text: "No Promo"}
          ]
        },
        genders: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("users.all_genders")},
            {value: 1, text: this.$t("users.search.genderMale")},
            {value: 2, text: this.$t("users.search.genderFemale")}
          ]
        },
        organic: {
          selected: 'all',
          options: [
            {value: 'all', text: 'All Source'},
            {value: 'organic', text: 'Only Organic'},
            {value: 'marketing', text: 'Only Marketing'}
          ]
        },
        ads: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Ads'
          }]
        },
        adsData1: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Data1'
          }]
        },
        adsData2: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Data2'
          }]
        },
        adsData3: {
          selected: 'all',
          options: [{
            value: 'all', text: 'All Data3'
          }]
        },
        subscriptions: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("finance.subscriptions_all")},
            {value: 'no', text: this.$t("finance.subscriptions_no")},
            {value: 'sub_1_week', text: this.$t("finance.sub_1_week")},
            {value: 'sub_1_month', text: this.$t("finance.sub_1_month")},
            {value: 'sub_3_month', text: this.$t("finance.sub_3_month")},
            {value: 'sub_6_month', text: this.$t("finance.sub_6_month")},
            {value: '', text: '---------'},
            {value: 'sub_12_month', text: this.$t("finance.sub_12_month")},
            {value: 'sub_lifetime', text: this.$t("finance.sub_lifetime")},
            {value: 'sub_1_month_ft3d', text: this.$t("finance.sub_1_month_ft3d")},
          ]
        },
        packs: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("finance.packs_all")},
            {value: 'no', text: this.$t("finance.packs_no")},
            {value: '', text: '---------'},
            {value: 'icebreakers', text: this.$t("finance.packs_icebreakers")},
            {value: 'icebreaker_12', text: this.$t("finance.icebreaker_12")},
            {value: 'icebreaker_35', text: this.$t("finance.icebreaker_35")},
            {value: 'icebreaker_65', text: this.$t("finance.icebreaker_65")},
            {value: '', text: '---------'},
            {value: 'boosts', text: this.$t("finance.packs_boosts")},
            {value: 'boost_5', text: this.$t("finance.boost_5")},
            {value: 'boost_10', text: this.$t("finance.boost_10")},
            {value: 'boost_20', text: this.$t("finance.boost_20")},
            {value: '', text: '----- OLD -----'},
            {value: 'icebreaker_3', text: this.$t("finance.icebreaker_3")},
            {value: 'icebreaker_5', text: this.$t("finance.icebreaker_5")},
            {value: 'icebreaker_10', text: this.$t("finance.icebreaker_10")},
            {value: 'icebreaker_15', text: this.$t("finance.icebreaker_15")},
            {value: 'icebreaker_30', text: this.$t("finance.icebreaker_30")},
            {value: '', text: '---------'},
            {value: 'credits', text: this.$t("finance.packs_credits")},
            {value: 'pack_mini', text: this.$t("finance.pack_mini")},
            {value: 'pack_basic', text: this.$t("finance.pack_basic")},
            {value: 'pack_medium', text: this.$t("finance.pack_medium")},
            {value: 'pack_mega', text: this.$t("finance.pack_mega")},
            {value: 'pack_giga', text: this.$t("finance.pack_giga")},
          ]
        },
        status: {
          selected: 'main',
          options: [
            {value: 'all', text: this.$t("finance.status_all")},
            {value: 'main', text: this.$t("finance.status_main")},
            {value: '', text: '---------'},
            {value: '=S', text: this.$t("finance.status_all_success")},
            {value: 'S', text: this.$t("finance.status_S")},
            {value: '=SR', text: this.$t("finance.status_all_success_renew")},
            {value: 'SR', text: this.$t("finance.status_SR")},
            {value: 'SS', text: this.$t("finance.status_SS")},
            {value: '', text: '---------'},
            {value: '=F', text: this.$t("finance.status_all_fail")},
            {value: 'F', text: this.$t("finance.status_F")},
            {value: 'FR', text: this.$t("finance.status_FR")},
            {value: 'FF', text: this.$t("finance.status_FF")},
            {value: 'FC', text: this.$t("finance.status_FC")},
            {value: 'FE', text: this.$t("finance.status_FE")},
            {value: 'E', text: this.$t("finance.status_E")},
            {value: 'RE', text: this.$t("finance.status_RE")},
            {value: '', text: '---------'},
            {value: '=C', text: this.$t("finance.status_all_cancel")},
            {value: 'X', text: this.$t("finance.status_X")},
            {value: '=CS', text: this.$t("finance.status_CS")},
            {value: '=CP', text: this.$t("finance.status_CP")},
            {value: '', text: '---------'},
            {value: '=R', text: this.$t("finance.status_all_refund")},
            {value: 'RF', text: this.$t("finance.status_RF")},
            {value: 'CB', text: this.$t("finance.status_CB")},
            {value: '', text: '---------'},
            {value: 'I', text: this.$t("finance.status_I")},
          ]
        }
      },
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body">
          <!-- Search -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="transactionsSearchForm" @submit.stop.prevent>
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsSearchTypeFilter"
                  v-model="filters.searchType.selected"
                  class="form-select me-lg-1"
                  :options="filters.searchType.options">
              </b-form-select>
            </div>
            <div class="col-sm-auto">
              <b-form-input
                id="transactionsSearchData"
                class="me-lg-1"
                v-model="filters.searchData"
                placeholder="Search…">
              </b-form-input>
            </div>
            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processSearch(true)">{{ $t("searchBtn") }}</b-button>
            </div>
          </b-form>
          <hr />
          <!-- filters -->
          <b-form inline class="row gy-2 gx-3 align-items-center" id="transactionsFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="transactionsDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- Currencies -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsCurrenciesFilter"
                  v-model="filters.currencies.selected"
                  class="form-select me-lg-1"
                  :options="filters.currencies.options">
              </b-form-select>
            </div>

            <!-- Countries -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsCountriesFilter"
                  v-model="filters.countries.selected"
                  class="form-select me-lg-1"
                  :options="filters.countries.options">
              </b-form-select>
            </div>

            <!-- Genders -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsGenderFilter"
                  v-model="filters.genders.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.genders.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Subscriptions -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsSubFilter"
                  v-model="filters.subscriptions.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.subscriptions.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Packs -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsPackFilter"
                  v-model="filters.packs.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.packs.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Promo -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsPromoFilter"
                  v-model="filters.promo.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.promo.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- PaymentMethod -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsMethodFilter"
                  v-model="filters.paymentMethod.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.paymentMethod.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- PaymentProcessor -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsProcessorFilter"
                  v-model="filters.paymentProcessor.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.paymentProcessor.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsStatusFilter"
                  v-model="filters.status.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.status.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <!-- Organic -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsOrganicFilter"
                  v-model="filters.organic.selected"
                  class="form-select me-lg-1"
                  :options="filters.organic.options">
              </b-form-select>
            </div>

            <!-- Ads -->
            <div class="col-sm-auto">
              <b-form-select
                  id="transactionsAdsFilter"
                  v-model="filters.ads.selected"
                  class="form-select me-lg-1"
                  :options="filters.ads.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto" v-show="filters.ads.selected !== 'all'">
              <b-form-select
                  id="leadsAdsData1Filter"
                  v-model="filters.adsData1.selected"
                  class="form-select me-lg-1"
                  :options="filters.adsData1.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto" v-show="filters.adsData1.selected !== 'all'">
              <b-form-select
                  id="leadsAdsData2Filter"
                  v-model="filters.adsData2.selected"
                  class="form-select me-lg-1"
                  :options="filters.adsData2.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto" v-show="filters.adsData2.selected !== 'all'">
              <b-form-select
                  id="leadsAdsData3Filter"
                  v-model="filters.adsData3.selected"
                  class="form-select me-lg-1"
                  :options="filters.adsData3.options">
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row" v-if="transactionsSearch === 'filters'">

        <!-- processed -->
        <div class="col-lg-3">
          <div class="card">
            <div class="card bg-success mini-stats-wid mb-0">
              <div class="card-body">
                <div class="media">
                  <div class="media-body">
                    <p class="text-white fw-medium mb-2">{{ $t("finance.processed") }}</p>
                    <h4 class="mb-0 text-white"><small class="text-white-50 d-inline-block me-2 ">{{ stats.success }}</small> {{ stats.revenue_ht }}€
                      <small class="text-white-50" style="font-size:12px;"> ({{ stats.revenue }}€)</small>
                    </h4>

                  </div>
                  <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                    <span class="avatar-title bg-light text-success">
                      <i class="bx bx-dollar-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="ps-2 pe-2">
              <div class="table-responsive">
                <table class="table project-list-table table-nowrap table-centered table-borderless align-middle">
                  <tr>
                    <td><span class="fw-medium text-success">{{ $t("finance.first") }}</span></td>
                    <td>{{ stats.first }}</td>
                    <td><h4 class="mb-0"> {{ stats.revenue_first_ht }}€ <small class="text-muted" style="font-size:12px;">  ({{ stats.revenue_first }}€)</small></h4></td>
                  </tr>
                  <tr>
                    <td><span class="fw-medium text-info">{{ $t("finance.renew") }}</span></td>
                    <td>{{ stats.renew }}</td>
                    <td><h4 class="mb-0"> {{ stats.revenue_renew_ht }}€ <small class="text-muted" style="font-size:12px;">  ({{ stats.revenue_renew }}€)</small></h4></td>
                  </tr>
                </table>
              </div>

            </div>
          </div>
        </div>

        <!-- failed -->
        <div class="col-lg-3">
          <div class="card">
            <div class="card bg-dark mini-stats-wid mb-0">
              <div class="card-body">
                <div class="media">
                  <div class="media-body">
                    <p class="text-white fw-medium mb-2">{{ $t("finance.fail") }}</p>
                    <h4 class="mb-0 text-white">{{ stats.fail }}</h4>
                  </div>
                  <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                    <span class="avatar-title bg-light text-dark">
                      <i class="bx bx-error-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="ps-2 pe-2">
              <div class="table-responsive">
                <table class="table project-list-table table-nowrap table-centered table-borderless align-middle">
                  <tr>
                    <td><span class="fw-medium text-dark">{{ $t("finance.fail_provisions") }}</span></td>
                    <td><h4 class="mb-0"> {{ stats.fail_provisions }}</h4></td>
                  </tr>
                  <tr>
                    <td><span class="fw-medium text-muted">{{ $t("finance.fail_others") }}</span></td>
                    <td><h4 class="mb-0"> {{ stats.fail_others }}</h4></td>
                  </tr>
                </table>
              </div>

            </div>
          </div>
        </div>

        <!-- Stops -->
        <div class="col-lg-3">
          <div class="card">
            <div class="card bg-pink mini-stats-wid mb-0">
              <div class="card-body">
                <div class="media">
                  <div class="media-body">
                    <p class="text-white fw-medium mb-2">{{ $t("finance.stops") }}</p>
                    <h4 class="mb-0 text-white">{{ stats.stopped }}</h4>
                  </div>
                  <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                    <span class="avatar-title bg-light text-pink">
                      <i class="bx bx-stop-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="ps-2 pe-2">
              <div class="table-responsive">
                <table class="table project-list-table table-nowrap table-centered table-borderless align-middle">
                  <tr>
                    <td><span class="fw-medium text-dark">{{ $t("finance.cancel") }}</span></td>
                    <td><h4 class="mb-0"> {{ stats.cancel }}</h4></td>
                  </tr>
                  <tr>
                    <td><span class="fw-medium text-muted">{{ $t("finance.expire") }}</span></td>
                    <td><h4 class="mb-0"> {{ stats.expire }}</h4></td>
                  </tr>
                </table>
              </div>

            </div>
          </div>
        </div>

        <!-- Negatives -->
        <div class="col-lg-3">
          <div class="card">
            <div class="card bg-danger mini-stats-wid mb-0">
              <div class="card-body">
                <div class="media">
                  <div class="media-body">
                    <p class="text-white fw-medium mb-2">{{ $t("finance.losses") }}</p>
                    <h4 class="mb-0 text-white">{{ stats.losses }}</h4>
                  </div>
                  <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                    <span class="avatar-title bg-light text-danger">
                      <i class="bx bx-minus-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="ps-2 pe-2">
              <div class="table-responsive">
                <table class="table project-list-table table-nowrap table-centered table-borderless align-middle">
                  <tr>
                    <td><span class="fw-medium text-dark">{{ $t("finance.refund") }}</span></td>
                    <td><h4 class="mb-0"> {{ stats.refund }}</h4></td>
                  </tr>
                  <tr>
                    <td><span class="fw-medium text-muted">{{ $t("finance.chargeback") }}</span></td>
                    <td><h4 class="mb-0"> {{ stats.chargeback }}</h4></td>
                  </tr>
                </table>
              </div>

            </div>
          </div>
        </div>

      </div>

      <div class="row" v-if="transactionsSearch === 'filters'">

        <div class="col-12 col-lg-4">
          <!-- Avg First -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("finance.avg_first") }}</p>
                  <h4 class="mb-0"> {{ stats.avgFirst }}€</h4>
                </div>
                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                  <span class="avatar-title bg-light text-dark">
                    <i class="bx bx-purchase-tag font-size-24"></i>
                  </span>
                </div>
              </div>
              <apexchart
                  ref="transactionsAvgFirstHistoryChart"
                  height="200"
                  type="area"
                  dir="ltr"
                  :series="avgFirstGraph.series"
                  :options="avgFirstGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <!-- Avg Delay -->
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="media">
                <div class="media-body">
                  <p class="text-dark fw-medium mb-2">{{ $t("finance.avg_delay") }}</p>
                  <h4 class="mb-0">{{ stats.avgDelay }}<small> {{ $t("days") }}</small></h4>
                </div>
                <div class="avatar-sm align-self-center mini-stat-icon rounded-circle ">
                    <span class="avatar-title bg-light text-dark">
                      <i class="bx bx-calendar font-size-24"></i>
                    </span>
                </div>
              </div>
              <apexchart
                  ref="transactionsAvgDelayHistoryChart"
                  height="200"
                  type="area"
                  dir="ltr"
                  :series="avgDelayGraph.series"
                  :options="avgDelayGraph.chartOptions"
              ></apexchart>
            </div>
          </div>

        </div>

        <!-- graph SDK -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardChartSDK">
              <h5 class="card-title">SDKs</h5>
              <apexchart
                  ref="transactionsSDKChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="sdkGraph.series"
                  :options="sdkGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <div class="row" v-if="transactionsSearch === 'filters'">

        <!-- graph Processors -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardChartProcessors">
              <h5 class="card-title">Processors</h5>
              <apexchart
                  ref="transactionsProcessorsChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="processorsGraph.series"
                  :options="processorsGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- graph Quality -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardChartQuality">
              <h5 class="card-title">Quality Users</h5>
              <apexchart
                  ref="transactionsQualityChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="qualityGraph.series"
                  :options="qualityGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- graph Reasons -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardChartReasons">
              <h5 class="card-title">Reasons</h5>
              <apexchart
                  ref="transactionsReasonsChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="reasonsGraph.series"
                  :options="reasonsGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- Charts -->
      <div class="row" v-if="transactionsSearch === 'filters'">

        <!-- graph Subs -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardChartSubs">
              <h5 class="card-title">Subs</h5>
              <apexchart
                  ref="transactionsSubsChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="productsGraph.subs.series"
                  :options="productsGraph.subs.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- graph IceBreakers -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardChartIceBreakers">
              <h5 class="card-title">IceBreakers</h5>
              <apexchart
                  ref="transactionsIceBreakersChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="productsGraph.iceBreakers.series"
                  :options="productsGraph.iceBreakers.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

        <!-- graph Boosts -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardCharBoosts">
              <h5 class="card-title">Boosts</h5>
              <apexchart
                  ref="transactionsBoostsChart"
                  height="200"
                  type="donut"
                  dir="ltr"
                  :series="productsGraph.boosts.series"
                  :options="productsGraph.boosts.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>

      </div>

      <!-- Charts Evol -->
      <div class="row" v-if="transactionsSearch === 'filters'">
        <!-- success -->
        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartEvolSuccess">
              <h5 class="card-title">Success Evol.</h5>
              <apexchart
                  ref="transactionsEvolSuccessChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="300"
                  :series="successGraph.series"
                  :options="successGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
        <!-- Fail -->
        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartEvolRevenue">
              <h5 class="card-title">Revenue Evol.</h5>
              <apexchart
                  ref="transactionsEvolRevenueChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="300"
                  :series="revenueGraph.series"
                  :options="revenueGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Evol2 -->
      <div class="row" v-if="transactionsSearch === 'filters'">
        <!-- success -->
        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartEvolFail">
              <h5 class="card-title">Fail Evol.</h5>
              <apexchart
                  ref="transactionsEvolFailChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="250"
                  :series="failGraph.series"
                  :options="failGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
        <!-- Fail -->
        <div class="col-12 col-lg-6">
          <div class="card">
            <div class="card-body" id="cardChartEvolCancel">
              <h5 class="card-title">Cancel Evol.</h5>
              <apexchart
                  ref="transactionsEvolCancelChart"
                  class="apex-charts"
                  type="bar"
                  dir="ltr"
                  height="250"
                  :series="cancelGraph.series"
                  :options="cancelGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="transactions-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row bg-white" v-if="!isLoading && transactions.length > 0">
        <div class="table-responsive">
          <table
              id="transactions-list"
              class="table project-list-table table-nowrap table-centered table-borderless align-middle"
          >
            <thead>
            <tr>
              <th scope="col">{{ $t('finance.createdAt') }}</th>
              <th scope="col">{{ $t('finance.status') }}</th>
              <th scope="col">{{ $t('finance.orderid') }}</th>
              <th scope="col">{{ $t('finance.product') }}</th>
              <th scope="col">{{ $t('finance.adData') }}</th>
              <th scope="col">{{ $t('finance.method') }}</th>
              <th scope="col">{{ $t('finance.user') }}</th>
              <th scope="col">Quality<br>SL & PM Group</th>
              <th scope="col">{{ $t('finance.country') }}</th>
              <th scope="col">{{ $t('finance.delay') }}</th>
              <th scope="col">{{ $t('action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="transaction in transactions" :key="transaction.id">
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(transaction.createdAt, 'DD MMM YYYY HH:mm') }}
                  <span v-show="!_isEmpty(transaction.androidSdkVersion)"><br>SDK: <b>{{ transaction.androidSdkVersion }}</b> <small>({{ transaction.androidSdk }})</small></span>
                </p>
              </td>
              <td>
                <span
                    class="badge"
                    :class="{
                      'bg-success text-light': `${transaction.status}` === 'S',
                      'bg-info text-light': `${transaction.status}` === 'SS' || `${transaction.status}` === 'SR',
                      'bg-purple text-light': (`${transaction.status}` === 'SS' || `${transaction.status}` === 'SR') && `${transaction.productType}` === 'P',
                      'bg-danger text-light': `${transaction.status}` === 'E' || `${transaction.status}` === 'RE' || `${transaction.status}` === 'FE',
                      'bg-light text-dark': `${transaction.status}` === 'F' || `${transaction.status}` === 'FR' || `${transaction.status}` === 'FF' || `${transaction.status}` === 'FC',
                      'bg-pink text-light': `${transaction.status}` === 'X' || `${transaction.status}` === 'C' || `${transaction.status}` === 'CS' || `${transaction.status}` === 'CP',
                      'bg-warning text-dark': `${transaction.status}` === 'I' || `${transaction.status}` === 'RP',
                      'bg-dark text-light': `${transaction.status}` === 'RF' || `${transaction.status}` === 'CB',
                    }"
                >{{ $t(`finance.status_${transaction.status}`) }}</span
                ><p v-show="transaction.code != '-1' && !['I','RP'].includes(transaction.status)"><small>Code <b>{{ transaction.code }}</b></small>
                  <small v-show="transaction.error != '' "><br>{{ transaction.error }}</small></p>
              </td>
              <td>
                <p class="text-muted font-size-12"><strong>{{ transaction.orderId }}</strong>
                <small v-show="transaction.reason != '' "><br>{{ formatReason(transaction.reason) }}</small></p>
              </td>
              <td>
                <p class="text-muted font-size-12">
                  <span v-show="transaction.offer === 1" class="badge bg-pink text-light">PROMO</span>
                  <br v-show="transaction.offer === 1">
                  {{ $t(`finance.${transaction.productId}`) }}
                  <span v-show="transaction.autoReload === 1"><br>AUTO-RELOAD</span>

                </p>
                <p class="font-size-14" >
                  <span
                      class="text-dark fw-bold d-inline-block me-2"
                      :class="{
                        'text-danger': `${transaction.status}` === 'RF' || `${transaction.status}` === 'CB',
                        'text-primary': `${transaction.status}` === 'S' || `${transaction.status}` === 'SR' || `${transaction.status}` === 'SS'
                      }">{{ transaction.amount/100 }} {{ transaction.currency }}
                    <span class="font-size-10 text-white bg-info p-1" v-show="transaction.currency !== 'EUR'">{{ transaction.amountEUR / 100}} &euro;</span>
                  </span>

                  <span
                      class="text-dark font-size-10"
                      :class="{
                      'text-danger': `${transaction.status}` === 'RF' || `${transaction.status}` === 'CB',
                      'text-primary': `${transaction.status}` === 'S' || `${transaction.status}` === 'SR' || `${transaction.status}` === 'SS'
                    }">({{ transaction.amountHT/100 }} {{ transaction.currency }})</span>
                    <small><br>{{ transaction.priceGroup }}</small>
                </p>
              </td>
              <td>
                <p class="text-muted font-size-12">
                  <b>{{ transaction.adSource }}</b><br>
                  <small>{{ trunc(transaction.adCampaign) }}</small>
                </p>
              </td>
              <td>
                <p class="text-muted font-size-12">
                  <img :src="getPaymentMethodImg(transaction.paymentMethod, transaction.paymentProcessor)" width="24" height="24" alt=""/>
                  {{ $t(`finance.processor_${transaction.paymentProcessor}`) }}
                </p>
              </td>
              <td>
                <div class="media">
                  <div class="avatar-xs" @click="showUser(transaction.userId)">
                    <img
                        :id="`transaction-user-${transaction.id}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-14 mb-0" @click="showUser(transaction.userId)">
                      <span
                        :class="{
                          'text-primary': transaction.sex === 1,
                          'text-danger': transaction.sex === 2,
                          'text-dark': transaction.sex === 0,
                        }"
                      >
                        {{ transaction.name || '-' }}
                      </span> {{ getAge(transaction.birth) }}</h5>
                    <small class="text-dark">{{transaction.locality}}</small><br>
                    <small class="text-muted">#{{ transaction.userId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <strong v-if="!['SS','SR','X','FR','RE','RF','CB'].includes(transaction.status)">{{ transaction.appVersion || "2.6.0" }}</strong><br>
                <span
                    class="badge"
                    :class="{
                      'bg-success text-light': transaction.isQualityUser === 1,
                      'bg-light text-dark': transaction.isQualityUser === 0
                    }"
                >{{ $t(`marketing.quality_user_${transaction.isQualityUser}`) }}</span>
                <br><small>SLG: {{ transaction.slGroup }}</small>
                <br><small>PMG: {{ transaction.pmGroup }}</small>
              </td>
              <td>
                <country-flag :country='transaction.country' size='small'/>
              </td>
              <td>
                <p class="text-muted font-size-13">{{ transaction.delayRegistration || '-' }}</p>
              </td>
              <td>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="loadDetails(transaction.id)"
                  >{{ $t("finance.details") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="searchUser(transaction.userId)"
                  >{{ $t("finance.search_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="searchOrder(transaction.orderId)"
                  >{{ $t("finance.search_order") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(transaction.userId)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && transactions.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No transactions were found for these filters or search. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="transactions-list"
            ></b-pagination>
          </div>
        </div>
      </div>

    </div>

    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>

  </Layout>
</template>

<style>

.b-sidebar-right {
  width:35%;
}

.bg-purple {
  background-color: #7654da !important;
}

.b-sidebar-body {
  padding:16px;
}

</style>
